/**
 * The players' contact with floating bubbles that the C64 does in player code (spec 02 §3 Bounce,
 * Push): the bounce search of D_2162 (L217C) and the push of D_0AAB. Bubbles are in the same VIC
 * frame as the players (rebb64 compares D_AA0C / D_AA1E with FA / C2 directly). Popping by touch is
 * the BUBBLE stream's (rebb64 L0D02, in the software-entity update).
 */

import { bounceable, bubbleCell, pushable } from '../bubbles/view';
import { solidTile, ROWS } from '../playfield';
import { BOUNCE_DX, BOUNCE_DY, PUSH_DY, PUSH_REACH_LEFT, PUSH_REACH_RIGHT, PUSH_STEP } from './tuning';
import type { Bubble, Player, PlayerId, World } from '../types';

/**
 * L217C: the first bubble a player holding UP comes down on: |Bx - Px| < $0E and 0 <= By - Py < $14,
 * of a type the bubble code says can be bounced on (`bounceable`: not a small or just-blown shot, not
 * bursting, not blinked off).
 */
export function bubbleUnderFeet(world: World, p: Player): Bubble | undefined {
  return world.bubbles.find((b) => bounceable(b)
    && Math.abs(b.x - p.x) < BOUNCE_DX && ((b.y - p.y) & 0xff) < BOUNCE_DY);
}

/** The collision map, with the rows past the edges copying the edge rows (D_3A6C). */
const solidCell = (world: World, col: number, row: number): boolean =>
  solidTile(world.playfield, col, Math.max(0, Math.min(ROWS - 1, row)));

/**
 * D_0AAB, once per main-loop pass (an M-tick; it is not part of the players' I-tick): each player in
 * play (not while invulnerable: the C64 checks state 1) and not blowing shoves every floating bubble
 * in front of it by 4 px - facing left, one 0..$13 px to its left; facing right, one 0..$11 px to its
 * right; |dy| < $0E. A bubble pushed into a wall column snaps back to the column grid. The BUBBLE
 * stream (or the world) calls this at the start of each pass; it writes only `bubble.x`.
 */
export function pushBubbles(world: World): void {
  for (const id of [1, 0] as const) pushFor(world, id);
}

function pushFor(world: World, id: PlayerId): void {
  const p = world.players[id];
  if (p.state !== 'play' || p.invulnerable > 0 || p.motion.frame >= 8) return;
  const left = p.motion.frame >= 4;
  for (const b of world.bubbles) {
    if (!pushable(b) || Math.abs(p.y - b.y) >= PUSH_DY) continue;
    if (left ? ((p.x - b.x) & 0xff) >= PUSH_REACH_LEFT : ((b.x - p.x) & 0xff) >= PUSH_REACH_RIGHT) continue;
    b.x = (b.x + (left ? -PUSH_STEP : PUSH_STEP)) & 0xff;
    pushIntoWall(world, b, left);
  }
}

/**
 * The wall test after a push: the bubble's row ZP_EE indexes the map at row ZP_EE - 4 (table
 * $AD1E), cells +$28 and +$50 (+$00 too when its sub-row is set) in its column
 * pushing left, or one column right pushing right. Pushing left, rows outside 4..$1C skip the test;
 * pushing right, they are clamped into it.
 */
function pushIntoWall(world: World, b: Bubble, left: boolean): void {
  // The bubble's own row and sub-row, as the bubble code keeps them (they can part from Y).
  const cell = bubbleCell(b);
  let ey = cell.row;
  if (left && (ey < 4 || ey >= 0x1d)) return;
  ey = Math.max(4, Math.min(0x1c, ey));
  const col = (((b.x - 0x14) & 0xff) >> 3) + (left ? 0 : 1);
  const row = ey - 4;
  const hit = (cell.subY !== 0 && solidCell(world, col, row))
    || solidCell(world, col, row + 1) || solidCell(world, col, row + 2);
  if (!hit) return;
  b.x = left ? ((((b.x - 0x14 + 8) & 0xf8) + 0x14) & 0xff) : ((((b.x - 0x1c) & 0xf8) + 0x14) & 0xff);
}
